/**
 * DialogManager
 *  - Open with [data-dialog-open="dialogId"]
 *  - Close with [data-dialog-close] (inside the dialog)
 *  - Click outside (backdrop) closes
 *  - ESC closes (native) + restores focus
 *  - Returns focus to the opener
 */
export class DialogManager {
  private static instance: DialogManager | null = null;

  private dialogs = new Map<string, HTMLDialogElement>();

  private lastOpener = new Map<string, HTMLElement | null>();

  private static readonly FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])' +
    ', select:not([disabled]), textarea:not([disabled])' +
    ', [tabindex]:not([tabindex="-1"])' +
    ', [contenteditable], [contenteditable="true"]';

  constructor(root: Document | HTMLElement = document) {
    try {
      // Register any <dialog id="..."> on the page
      root.querySelectorAll<HTMLDialogElement>('dialog[id]').forEach((d) => {
        this.dialogs.set(d.id, d);
        this.wireDialog(d);
      });

      // Event delegation: openers
      root.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement)?.closest<HTMLElement>(
          '[data-dialog-open]'
        );

        if (!btn) {
          return;
        }

        e.preventDefault();
        const id = btn.getAttribute('data-dialog-open')!;
        this.open(id, btn);
      });

      // Event delegation: closers
      root.addEventListener('click', (e) => {
        const btn = (e.target as HTMLElement)?.closest<HTMLElement>(
          '[data-dialog-close]'
        );
        if (!btn) {
          return;
        }
        e.preventDefault();
        const dialog = btn.closest('dialog');
        if (dialog?.id) this.close(dialog.id);
      });
    } catch (error) {
      console.error(error);
    }
  }

  /** Returns the singleton instance, creating it on first call. Requires `document` on first call. */
  public static getInstance(document?: Document): DialogManager {
    try {
      if (!DialogManager.instance) {
        if (!document) {
          throw new Error('Document required for first initialization');
        }
        DialogManager.instance = new DialogManager(document);
      }
      return DialogManager.instance;
    } catch (error) {
      console.error('Failed to create DialogManager:', error);
      throw error;
    }
  }

  /**
   * Opens a registered dialog by ID. Remembers the opener element to restore focus on close.
   * @param id The `id` attribute of the target `<dialog>`.
   * @param opener The element that triggered the open, used to restore focus when the dialog closes.
   */
  public open(id: string, opener?: HTMLElement | null) {
    try {
      const dialog = this.dialogs.get(id);
      if (!dialog) {
        return;
      }

      // Remember opener to be able to restore focus.
      this.lastOpener.set(
        id,
        opener ?? (document.activeElement as HTMLElement | null)
      );

      // Mirror 'open' state on the opener.
      const openerEl =
        opener ??
        document.querySelector<HTMLElement>(`[data-dialog-open="${id}"]`);
      if (openerEl) {
        openerEl.classList.add('menu-isShown'); // Legacy visual hook.
        if (openerEl.hasAttribute('aria-expanded')) {
          openerEl.setAttribute('aria-expanded', 'true'); // A11Y state hook.
        }
      }

      // Only focus if dialog is newly opened.
      const justOpened = !dialog.open;

      if (justOpened) {
        dialog.showModal();
      }

      if (justOpened) {
        // Don't steal focus if UA or user action already focused on something inside.
        requestAnimationFrame(() => {
          if (!dialog.contains(document.activeElement)) {
            DialogManager.getFirstFocusable(dialog).focus({
              preventScroll: true
            });
          }
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Closes a registered dialog by ID.
   * @param id The `id` attribute of the target `<dialog>`.
   */
  public close(id: string) {
    try {
      const dialog = this.dialogs.get(id);
      if (!dialog) {
        return;
      }
      dialog.close();
    } catch (error) {
      console.error(error);
    }
  }

  /** Registers a dialog element that was added to the DOM after initialization. */
  public register(dialog: HTMLDialogElement): void {
    try {
      if (!dialog.id) {
        return;
      }

      if (this.dialogs.has(dialog.id)) {
        return;
      }

      this.dialogs.set(dialog.id, dialog);
      this.wireDialog(dialog);
    } catch (error) {
      console.error('[DialogManager] Failed to register dialog:', error);
    }
  }

  /** Looks up a `<dialog>` element by ID in the DOM and registers it. Returns `false` if not found or not a dialog element. */
  public registerById(id: string): boolean {
    try {
      const dialog = document.getElementById(id) as HTMLDialogElement | null;

      if (!dialog) {
        return false;
      }

      if (dialog.tagName.toLowerCase() !== 'dialog') {
        return false;
      }

      this.register(dialog);
      return true;
    } catch (error) {
      console.error('[DialogManager] Failed to register dialog by id:', error);
      return false;
    }
  }

  /** Attaches backdrop-click and close event listeners to a dialog, including focus restoration. */
  private wireDialog(dialog: HTMLDialogElement) {
    try {
      // Backdrop click -> close
      dialog.addEventListener('click', (event) => {
        // Only close when clicking the backdrop (target === dialog)
        if (event.target === dialog) {
          this.close(dialog.id);
        }
      });

      // Restore focus to opener.
      dialog.addEventListener('close', () => {
        try {
          // 1) Update all triggers consistently
          const escapedDialogId = CSS.escape(dialog.id);
          const triggers = Array.from(
            document.querySelectorAll<HTMLElement>(
              `[data-dialog-open="${escapedDialogId}"], [aria-controls="${escapedDialogId}"]`
            )
          );

          triggers.forEach((t) => {
            if (t.hasAttribute('aria-expanded')) {
              t.setAttribute('aria-expanded', 'false');
            }

            if (t.getAttribute('aria-haspopup') !== 'dialog') {
              t.setAttribute('aria-haspopup', 'dialog');
            }

            if (!t.hasAttribute('aria-controls')) {
              t.setAttribute('aria-controls', dialog.id);
            }

            t.classList.remove('menu-isShown');
          });

          // 2) Restore focus to opener (or first trigger)
          const opener = this.lastOpener.get(dialog.id) ?? triggers[0] ?? null;
          if (opener) {
            queueMicrotask(() => opener.focus());
          }

          this.lastOpener.delete(dialog.id);
        } catch (error) {
          console.error(error);
        }
      });
    } catch (error) {
      console.error(error);
    }
  }

  /** Returns the first focusable element inside a dialog, preferring `[autofocus]`. Falls back to the dialog itself. */
  private static getFirstFocusable(dialog: HTMLDialogElement): HTMLElement {
    try {
      // Check if there's an autofocus present and focusable.
      const auto = dialog.querySelector<HTMLElement>('[autofocus]');
      if (auto && DialogManager.isFocusable(auto)) {
        return auto;
      }

      // Fetch all focus candidates.
      const candidates = Array.from(
        dialog.querySelectorAll<HTMLElement>(DialogManager.FOCUSABLE_SELECTOR)
      ).filter(DialogManager.isFocusable);

      // Use the first focusable candidate OR fallback to the dialog itself.
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      return candidates[0] ?? dialog;
    } catch (error) {
      console.error(error);
      return dialog;
    }
  }

  /** Returns true if the element is visible, enabled, and reachable via keyboard navigation. */
  private static isFocusable(elem: HTMLElement): boolean {
    try {
      if ((elem as HTMLInputElement).type === 'hidden') {
        return false;
      }

      if ((elem as HTMLButtonElement).disabled) {
        return false;
      }

      if (elem.closest('[inert],[aria-hidden="true"]')) {
        return false;
      }

      const computedStyle = getComputedStyle(elem);
      if (
        computedStyle.display === 'none' ||
        computedStyle.visibility === 'hidden'
      ) {
        return false;
      }

      return elem.tabIndex >= 0;
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}

/** Returns the shared DialogManager singleton, initialized against the current document. */
export function getDialogManager(): DialogManager {
  return DialogManager.getInstance(document);
}

export default DialogManager;
